import { numberFromString } from "helpers";
import sortBy from "lodash/sortBy";
import { Integer } from "model/modelTypes";
import { DefaultNetwork } from "./options";
import {
  ISkAdNetworkConfiguration,
  ISkadNetworkTemplate,
  MAX_SKAD_ID_HIGH,
  MAX_SKAD_ID_LOW,
  MIN_SKAD_ID_HIGH,
  MIN_SKAD_ID_LOW,
} from "./skadNetwork";
import { SkanVersion } from "./skanVersion";

export type TSkadBlockRange = "LOW" | "HIGH";
export type TSkadBlock = {
  range: TSkadBlockRange;
  version: SkanVersion;
  networkId: DefaultNetwork;
};
export type TSkadBlockVersionAgnostic = {
  range: TSkadBlockRange;
  networkId: DefaultNetwork;
};
export enum TemplateType {
  PUBLISHER_ID = "PUBLISHER_ID",
  EXCHANGE = "EXCHANGE",
  PUBLISHER_EXCHANGE = "PUBLISHER_EXCHANGE",
}

export const SKAN_BLOCK_SIZE = 40;
export const EXCHANGES_NUMBER = 10;
export const DEFAULT_BLOCK_MAP: TSkadBlock[] = [];
export const getDefaultSKADBlockConfig = (t: TemplateType) => {
  return {
    templateType: t,
    blockSize: SKAN_BLOCK_SIZE,
    blockMap: DEFAULT_BLOCK_MAP,
  };
};

export type TSkadBlockConfigPublisher = {
  templateType?: TemplateType.PUBLISHER_ID;
  blockSize: Integer;
  blockMap: TSkadBlock[];
};
export type TSkadBlockConfigPublisherExchange = {
  templateType?: TemplateType.PUBLISHER_EXCHANGE;
  blockSize: Integer;
  blockMap: TSkadBlock[];
};
export type TSkadBlockConfigExchange = {
  templateType?: TemplateType.EXCHANGE;
  blockSize: Integer;
  blockMap: TSkadBlockVersionAgnostic[];
};
export type TSkadBlockConfig =
  | TSkadBlockConfigPublisher
  | TSkadBlockConfigPublisherExchange
  | TSkadBlockConfigExchange;

const MIN_ALLOWED_SKAN_VERSION = SkanVersion["v2.2"];

export const HARDCODED_VERSIONS_NUMBER = 3; //HARDCODE for now, 3 versions: 2.2, 3.0 and 4.0. Update after https://youtrack.dataseat.com/issue/PLAT-1417 is done on BE

const filterBlocksResponse = (resp) =>
  resp &&
  resp
    .map((i) => i.skadBlock as TSkadBlock)
    .filter(
      (s) =>
        numberFromString(s.version) >=
        numberFromString(MIN_ALLOWED_SKAN_VERSION)
    );
const NETWORK_ORDER = {
  [DefaultNetwork.Dataseat]: 1,
  [DefaultNetwork.Pubnative]: 2,
};
const RANGE_ORDER = {
  LOW: 1,
  HIGH: 2,
};
const sortBlocksResponse = (blocks?: TSkadBlock[]) =>
  blocks &&
  sortBy(blocks, [
    (b) => -1 * numberFromString(b.version),
    (b) => NETWORK_ORDER[b.networkId],
    (b) => RANGE_ORDER[b.range],
  ]);
const sortAgnosticBlocksResponse = (blocks?: TSkadBlockVersionAgnostic[]) =>
  blocks &&
  sortBy(blocks, [
    (b) => NETWORK_ORDER[b.networkId],
    (b) => RANGE_ORDER[b.range],
  ]);
const areSameBlocks = (
  a: TSkadBlock | TSkadBlockVersionAgnostic,
  b: TSkadBlock | TSkadBlockVersionAgnostic
) =>
  a.networkId === b.networkId &&
  a.range === b.range &&
  (a as any).version === (b as any).version;
export function getInitialBlockMap<
  T extends TSkadBlock[] | TSkadBlockVersionAgnostic[]
>(
  initialSkadConfig: ISkAdNetworkConfiguration | undefined,
  templateType: TemplateType
) {
  return (
    initialSkadConfig?.skadBlockConfig?.templateType === templateType
      ? initialSkadConfig?.skadBlockConfig?.blockMap ?? []
      : []
  ) as T;
}
function addExistingBlocks<T extends TSkadBlock | TSkadBlockVersionAgnostic>(
  blocks: T[] | undefined = [],
  existingBlocks: T[] | undefined = []
) {
  return blocks.concat(
    existingBlocks.filter(
      (eb) => !blocks.some((b) => areSameBlocks(b, eb))
      //existingBlocks should not be returned by BE as available,
      //but still filtering dublicates for double protection
    )
  );
}
export const normalizeBlocksResponseForPublisher = (
  resp,
  initialSkadConfig
) => {
  return sortBlocksResponse(
    addExistingBlocks<TSkadBlock>(
      filterBlocksResponse(resp),
      getInitialBlockMap<TSkadBlock[]>(
        initialSkadConfig,
        TemplateType.PUBLISHER_ID
      )
    )
  ) as TSkadBlock[];
};
export const isSameRange = (
  range: TSkadBlockRange | undefined,
  minSkadId: Integer | undefined,
  maxSkadId: Integer | undefined
) =>
  (range === "LOW" &&
    minSkadId === MIN_SKAD_ID_LOW &&
    maxSkadId === MAX_SKAD_ID_LOW) ||
  (range === "HIGH" &&
    minSkadId === MIN_SKAD_ID_HIGH &&
    maxSkadId === MAX_SKAD_ID_HIGH);

export const normalizeBlocksResponseForExchange = (
  resp,
  initialSkadConfig,
  numberOfVersions
) => {
  const candidates = filterBlocksResponse(resp).reduce((acc, b) => {
    acc[`${b.networkId}_${b.range}`] = acc[`${b.networkId}_${b.range}`] ?? 0;
    acc[`${b.networkId}_${b.range}`] += 1;
    return acc;
  }, {} as Record<string, Integer>);
  const agnosticBlocks: TSkadBlockVersionAgnostic[] = Object.keys(candidates)
    .filter((k) => candidates[k] === numberOfVersions)
    .map((k) => ({
      networkId: k.split("_")[0] as DefaultNetwork,
      range: k.split("_")[1] as TSkadBlockRange,
    }));
  return addExistingBlocks<TSkadBlockVersionAgnostic>(
    sortAgnosticBlocksResponse(agnosticBlocks),
    getInitialBlockMap<TSkadBlockVersionAgnostic[]>(
      initialSkadConfig,
      TemplateType.EXCHANGE
    ) as TSkadBlockVersionAgnostic[]
  );
};

export const getBlockMatchingTemplate = (
  skadConfig: Partial<ISkAdNetworkConfiguration> | undefined,
  skadTemplate: ISkadNetworkTemplate | undefined,
  availableBlocksList: TSkadBlock[] = []
) => {
  //networkId splitted from templateId is in priority cause it's updated in Form Values during LI edit
  //networkId in skadConfig is set later during save, during normalization
  const templateNetworkId =
    skadConfig?.templateId?.split("~")?.[1] ?? skadConfig?.networkId;

  const matchingTemplateIndex = availableBlocksList.findIndex(
    (block) =>
      isSameRange(
        block.range,
        skadTemplate?.minSkadId,
        skadTemplate?.maxSkadId
      ) && block.networkId === templateNetworkId
  );
  return {
    matchingTemplateIndex,
    matchingTemplate: availableBlocksList[matchingTemplateIndex],
  };
};

export const hasBlockInMap = (blockMap: TSkadBlock[], block: TSkadBlock) =>
  blockMap.some(
    (b) =>
      b.networkId === block.networkId &&
      b.range === block.range &&
      b.version === block.version
  );
